import React, { useState, useRef } from "react";
import { useButtonShortcut } from "../hooks/useSearchShortcut";
import { useShiftKey } from "../hooks/useShiftKey";
import { useJumpShortcuts } from "../hooks/useJumpShortcuts";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import AppBar from "@mui/material/AppBar";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import AppsIcon from "@mui/icons-material/Apps";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DomainIcon from "@mui/icons-material/Domain";
import JumpMenu from "./JumpMenu";
import { ProfileMenu } from "./ProfileMenu";
import { Kbd } from "./Kbd";
import { getMyAccountsApi } from "../apis/authApi";
import { APP_NAME } from "../constants";

export const DeskTopBar: React.FC = () => {
  const { t } = useTranslation();
  const [jumpOpen, setJumpOpen] = useState(false);
  const shiftHeld = useShiftKey();
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 4 });
  useJumpShortcuts(() => setJumpOpen(false));

  const jumpButtonRef = useRef<HTMLButtonElement | null>(null);
  useButtonShortcut({
    buttonRef: jumpButtonRef,
    key: "j",
    shiftKey: true,
    ignoreIfTyping: true,
  });

  const translations = {
    currentAccountAria: t("topBar.current_account_aria", {
      defaultValue: "Current account",
    }),
    currentAccountTooltipSingle: t("topBar.current_account_tooltip_single", {
      defaultValue: "You are viewing data for this account.",
    }),
  };

  const { data: accounts } = useQuery({
    queryKey: ["getMyAccounts"],
    queryFn: getMyAccountsApi,
  });
  const currentAccount = accounts?.find((account) => account.isCurrent);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          backgroundImage: "none",
          color: "text.primary",
          backgroundColor: (theme) =>
            scrolled
              ? theme.palette.mode === "dark"
                ? "rgba(20,23,27,0.75)"
                : "rgba(255,255,255,0.75)"
              : "transparent",
          backdropFilter: scrolled ? "blur(8px)" : "none",
          boxShadow: scrolled ? "0 4px 16px rgba(0,0,0,0.2)" : "none",
          transition: (theme) =>
            theme.transitions.create(["background-color", "backdrop-filter", "box-shadow"], {
              duration: theme.transitions.duration.short,
            }),
        }}
      >
        <Toolbar sx={{ position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {currentAccount && (
              <Tooltip title={translations.currentAccountTooltipSingle}>
                <Chip
                  aria-label={translations.currentAccountAria}
                  icon={<DomainIcon />}
                  label={currentAccount.accountName}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{
                    fontWeight: 600,
                    maxWidth: { xs: 140, sm: 220 },
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Button
            disableRipple
            ref={jumpButtonRef}
            color="inherit"
            aria-label={t("jumpMenu.open_button_aria", {
              defaultValue: "Open quick jump",
            })}
            onClick={() =>
              setJumpOpen((s) => {
                const opening = !s;
                if (opening) {
                  try {
                    // ensure any currently focused element is blurred before MUI
                    // sets aria-hidden on siblings to avoid hiding a focused element
                    (document.activeElement as HTMLElement | null)?.blur();
                  } catch {}
                }
                return opening;
              })
            }
            startIcon={<AppsIcon />}
            endIcon={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {shiftHeld && <Kbd aria-hidden="true">J</Kbd>}
                <KeyboardArrowDownIcon />
              </Box>
            }
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: 999,
              px: { xs: 1.25, md: 2 },
              py: 0.75,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.4rem",
              border: "none",
              backdropFilter: "blur(8px)",
              boxShadow: "none",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(40,46,53,0.85)" : "transparent",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(55,62,70,0.92)" : "rgba(0,0,0,0.04)",
              },
              "& .MuiButton-startIcon": {
                mr: { xs: 0.5, md: 1 },
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
              {APP_NAME}
            </Box>
          </Button>
          <JumpMenu open={jumpOpen} onClose={() => setJumpOpen(false)} />
          <Box sx={{ marginLeft: "auto" }}>
            <ProfileMenu />
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar aria-hidden="true" />
    </Box>
  );
};

